import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { toast } from "@volca/ui/lib/toast";
import { useCanManageProfitability } from "@/profitability/access";
import { useRemovePeriod } from "@/profitability/overview/period-review";
import { usePeriodParams } from "@/profitability/template-page/use-period-params";
import type { EnrichedProfitabilityPeriod } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import { RunStatus } from "./run-status";
import { statusCol } from "./status-col";

/**
 * Takes a period off the agent: a period run on a version that got it wrong,
 * or one the agent should never have had (a month on a weekly agent). A
 * rebuild never touches a period already run, so this is the only way one
 * goes. Removing is finance's, like choosing a period's result.
 */
export const RemovePeriod = ({
  period,
}: {
  period: EnrichedProfitabilityPeriod;
}) => {
  const [open, setOpen] = useState(false);
  const remove = useRemovePeriod(period.business_id);
  const canManage = useCanManageProfitability();
  const { periodId, compare, setPeriod, setCompare } = usePeriodParams();
  if (!canManage || period.status === "pending" || period.status === "running")
    return null;
  const label = period.display.period;
  return (
    <>
      <Button
        size="sm"
        variant="link"
        className="h-auto p-0 text-subtle"
        onClick={(event) => {
          // The row opens the period's result; this button removes it.
          event.stopPropagation();
          setOpen(true);
        }}
      >
        Remove
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onClick={(event) => event.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Remove {label}?</DialogTitle>
            <DialogDescription>
              The period and its reruns leave this agent: its page, the period
              list and every comparison. Running these dates again starts it
              afresh.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={remove.isPending}
              onClick={() =>
                remove.mutate(period.id, {
                  onSuccess: ({ run_ids }) => {
                    toast.success(`${label} was removed`);
                    // The page behind falls back to the latest period, and
                    // stops comparing with the removed one.
                    if (periodId && run_ids.includes(periodId)) setPeriod(null);
                    else if (
                      compare?.startsWith("run:") &&
                      run_ids.includes(compare.slice("run:".length))
                    )
                      setCompare(null);
                    setOpen(false);
                  },
                  onError: (error) =>
                    toast.error("The period could not be removed", {
                      description: reportError(error),
                    }),
                })
              }
            >
              Remove period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * The status, and under it the way to remove the period: in a column of its
 * own the template's list no longer fits the history sheet.
 */
export const statusRemoveCol: ColumnDef<EnrichedProfitabilityPeriod> = {
  ...statusCol,
  cell: ({ row }) => (
    <Stack gap={1} align="start">
      <RunStatus run={row.original} />
      <RemovePeriod period={row.original} />
    </Stack>
  ),
};
